import React, { useCallback, useState, createContext, useContext } from 'react';
import type { Customer, StaffAccount, StaffRole } from './types';
import { MOCK_CUSTOMERS, MOCK_STAFF } from './mockData';
interface AuthState {
  isCustomerLoggedIn: boolean;
  isAdminLoggedIn: boolean;
  currentCustomer: Customer | null;
  currentStaff: StaffAccount | null;
}
interface AuthContextType extends AuthState {
  customerLogin: (phone: string) => boolean;
  customerLogout: () => void;
  adminLogin: (email: string, role: StaffRole) => boolean;
  adminLogout: () => void;
  updateCustomer: (updates: Partial<Customer>) => void;
}
const AuthContext = createContext<AuthContextType | null>(null);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [authState, setAuthState] = useState<AuthState>({
    isCustomerLoggedIn: false,
    isAdminLoggedIn: false,
    currentCustomer: null,
    currentStaff: null
  });
  const customerLogin = useCallback((phone: string): boolean => {
    const customer = MOCK_CUSTOMERS.find(c => c.phone === phone);
    if (customer) {
      setAuthState(prev => ({
        ...prev,
        isCustomerLoggedIn: true,
        currentCustomer: customer
      }));
      return true;
    }
    // For demo: create a new customer for any phone
    const newCustomer: Customer = {
      id: `c_${Date.now()}`,
      phone,
      name: '',
      tier: 'member',
      points: 0,
      walletBalance: 0,
      totalDeposited: 0,
      totalBonusEarned: 0,
      totalSpent: 0,
      referralCode: `MHP${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
      createdAt: new Date().toISOString(),
      tags: [],
      notificationPreferences: {
        sms: true,
        zalo: false,
        email: false
      }
    };
    setAuthState(prev => ({
      ...prev,
      isCustomerLoggedIn: true,
      currentCustomer: newCustomer
    }));
    return true;
  }, []);
  const customerLogout = useCallback(() => {
    setAuthState(prev => ({
      ...prev,
      isCustomerLoggedIn: false,
      currentCustomer: null
    }));
  }, []);
  const adminLogin = useCallback((email: string, _role: StaffRole): boolean => {
    const staff = MOCK_STAFF.find(s => s.email === email) || MOCK_STAFF[0];
    setAuthState(prev => ({
      ...prev,
      isAdminLoggedIn: true,
      currentStaff: staff
    }));
    return true;
  }, []);
  const adminLogout = useCallback(() => {
    setAuthState(prev => ({
      ...prev,
      isAdminLoggedIn: false,
      currentStaff: null
    }));
  }, []);
  const updateCustomer = useCallback((updates: Partial<Customer>) => {
    setAuthState(prev => ({
      ...prev,
      currentCustomer: prev.currentCustomer ? {
        ...prev.currentCustomer,
        ...updates
      } : null
    }));
  }, []);
  return <AuthContext.Provider value={{
    ...authState,
    customerLogin,
    customerLogout,
    adminLogin,
    adminLogout,
    updateCustomer
  }} data-id="element-641">
      {children}
    </AuthContext.Provider>;
}
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}